<template>
    <div :class="{ 'wo-card-active': active }" class="wo-card relative" @click="$emit('click', $event)">
        <div v-if="needsAttention" class="wo-card-attention-banner" data-testid="wo-card-attention-banner">
            <span class="wo-card-attention-banner-label">
                <span class="wo-card-attention-banner-dot" />
                {{ trans('workorder.card.attention.banner') }}
            </span>
            <span v-if="attentionAgeLabel" class="wo-card-attention-banner-age" data-testid="wo-card-attention-age">
                {{ attentionAgeLabel }}
            </span>
        </div>
        <div v-else-if="isOverdue" class="wo-card-overdue-banner" data-testid="wo-card-overdue-banner">
            <span class="wo-card-overdue-banner-label">
                <span class="wo-card-overdue-banner-dot" />
                {{ trans('workorder.card.overdue.banner') }}
            </span>
            <span v-if="daysOverdueLabel" class="wo-card-overdue-banner-age" data-testid="wo-card-overdue-age">
                {{ daysOverdueLabel }}
            </span>
        </div>
        <div v-else-if="timeInStatusLabel" class="wo-card-time-banner" data-testid="wo-card-time-banner">
            <span class="wo-card-time-banner-label">
                <span class="wo-card-time-banner-dot" />
                {{ trans('workorder.card.time_in_status.banner') }}
            </span>
            <span class="wo-card-time-banner-age" data-testid="wo-card-time-age">
                {{ timeInStatusLabel }}
            </span>
        </div>
        <header class="wo-card-header">
            <div class="flex items-center justify-between gap-1">
                <div class="truncate">
                    <work-order-type-icon :text="workOrder.code" :work-order="workOrder" style="min-height: 30px" />
                    <div v-if="locationLabel" class="truncate font-medium">{{ locationLabel }}</div>
                </div>
                <div @click.stop="">
                    <action-menu v-if="hasMenuActions" :teleport="true">
                        <dropdown-item
                            v-if="showMoveToToDo"
                            data-testid="move-to-to-do"
                            @click="
                                $emit('move-to', {
                                    workOrder,
                                    status: 'to_do',
                                })
                            "
                            >{{ trans('workorder.action.move_to.to_do') }}
                        </dropdown-item>
                        <dropdown-item
                            v-if="showMoveToInProgress"
                            data-testid="move-to-in-progress"
                            @click="
                                $emit('move-to', {
                                    workOrder,
                                    status: 'in_progress',
                                })
                            "
                            >{{ trans('workorder.action.move_to.in_progress') }}
                        </dropdown-item>
                        <dropdown-item
                            v-if="showMoveToDone"
                            data-testid="move-to-done"
                            @click="
                                $emit('move-to', {
                                    workOrder,
                                    status: 'done',
                                })
                            "
                            >{{ trans('workorder.action.move_to.done') }}
                        </dropdown-item>
                        <template v-if="canDelete">
                            <dropdown-divider />
                            <dropdown-item data-testid="delete-work-order" @click="$emit('delete', workOrder)">
                                <div class="d-flex align-items-center justify-content-between w-100">
                                    {{ trans('general.action.delete') }}
                                    <icon icon="cil-trash" />
                                </div>
                            </dropdown-item>
                        </template>
                    </action-menu>
                </div>
            </div>
        </header>
        <section class="wo-card-body mt-4">
            <h3 class="wo-card-title mt-2 mb-3 line-clamp-2">
                {{ workOrder.title }}
            </h3>
            <dl class="mb-0">
                <div v-if="!isDone" class="row">
                    <dt class="font-weight-bold text-truncate col-5">
                        {{ trans('workorder.card.label.due_date') }}
                    </dt>
                    <dd class="text-truncate font-weight-bold col-7 mb-1">
                        <span
                            v-if="workOrder.dueDate"
                            :class="{
                                'text-warning': isUpcoming,
                                'text-danger': isOverdue,
                            }"
                            >{{ fullDate(workOrder.dueDate, currentLocale) }}</span
                        >
                        <span v-else>-</span>
                    </dd>
                </div>
                <div v-if="isDone" class="row">
                    <dt class="font-weight-bold text-truncate col-5">
                        {{ trans('workorder.card.label.resolved_date') }}
                    </dt>
                    <dd class="text-truncate font-weight-bold col-7 mb-1">
                        <span
                            v-if="resolvedDateLabel"
                            :class="{
                                'text-warning': isUpcoming,
                                'text-danger': isOverdue,
                            }"
                            >{{ resolvedDateLabel }}</span
                        >
                        <span v-else>-</span>
                    </dd>
                </div>
                <div class="row">
                    <dt class="text-truncate col-5">
                        {{ trans('workorder.card.label.assignee') }}
                    </dt>
                    <dd class="text-truncate col-7 mb-1">
                        <span v-if="workOrder.assignee?.name">{{ workOrder.assignee.name }}</span>
                        <span v-else class="fw-bold text-danger" data-testid="wo-card-unassigned">{{ trans('workorder.assignee.unassigned') }}</span>
                    </dd>
                </div>
                <div v-if="priorityEnabled" class="row">
                    <dt class="text-truncate col-5">
                        {{ trans('workorder.card.label.priority') }}
                    </dt>
                    <dd class="text-truncate col-7 mb-1">
                        <work-order-priority :priority="workOrder.priority" />
                    </dd>
                </div>
                <div v-if="workOrder.caseCategory" class="row">
                    <dt class="text-truncate col-5">
                        {{ trans('workorder.card.label.case_category') }}
                    </dt>
                    <dd class="text-truncate col-7 mb-1">
                        {{ workOrder.caseCategory.label }}
                    </dd>
                </div>
                <div class="row">
                    <dt class="text-truncate col-5">
                        {{ trans('workorder.details.workorder.status.label') }}
                    </dt>
                    <dd class="text-truncate col-7 mb-1">
                        <status-badge
                            v-if="assetOperability && assetUsageCategory"
                            :inline="true"
                            :operability="assetOperability"
                            :usage="assetUsageCategory"
                        />
                        <span v-else>-</span>
                    </dd>
                </div>
            </dl>
        </section>
        <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center bg-black/10">
            <spinner />
        </div>
    </div>
</template>
<script setup>
import WorkOrderPriority from '@/Pages/WorkOrders/Modals/WorkOrderPriority.vue';
import WorkOrderTypeIcon from '@/Pages/WorkOrders/Modals/WorkOrderTypeIcon.vue';
import { DateTime } from 'luxon';
import DropdownItem from '@/Components/DropdownItem.vue';
import DropdownDivider from '@/Components/DropdownDivider.vue';
import ActionMenu from '@/Components/ActionMenu.vue';
import StatusBadge from '@/Components/StatusBadge.vue';
import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';
import fullDate from '@/filters/fullDate.js';
import { trans } from '@/mixins/i18n.js';
import { hasFeature } from '@/mixins/hasFeature.js';
import { buildLocationHierarchyLabel } from '@/Utils/buildLocationHierarchyLabel.ts';
import { workOrderNeedsAttention } from '@/Pages/WorkOrders/workOrderAttention.ts';
import { workOrderIsOverdue, workOrderDaysOverdue } from '@/Pages/WorkOrders/workOrderOverdue.ts';
import { workOrderTimeInStatusAge } from '@/Pages/WorkOrders/workOrderTimeInStatus.ts';
import { formatCompactAge } from '@/Utils/formatCompactAge.ts';

defineEmits(['move-to', 'click', 'delete']);

const props = defineProps({
    active: {
        type: Boolean,
        default: false,
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
    workOrder: {},
});

const page = usePage();
const currentLocale = computed(() => page.props.currentLocale || 'en');
const priorityEnabled = hasFeature('work_order_priority');

const isToDo = computed(() => props.workOrder.status.value === 'to_do');
const isInProgress = computed(() => props.workOrder.status.value === 'in_progress');
const isDone = computed(() => props.workOrder.status.value === 'done');
const needsAttention = computed(() => workOrderNeedsAttention(props.workOrder));

// Compact single-unit age (12m / 6h / 3d / 2w) shown on the attention banner to convey how long the
// work order has been waiting with incomplete information.
const attentionAgeLabel = computed(() => (needsAttention.value ? formatCompactAge(props.workOrder.createdAt) : ''));
const resolvedDateLabel = computed(() => {
    if (props.workOrder.performedAt) {
        return fullDate(props.workOrder.performedAt, currentLocale.value);
    }

    return props.workOrder.statusChangedAt ? fullDate(props.workOrder.statusChangedAt, currentLocale.value) : '';
});
const canBeEdited = computed(() => !isDone.value && props.workOrder.isEditable);

// Status actions the current user may actually perform; canMoveToInProgress/canMoveToDone come
// from the backend policy. The three-dots menu is hidden entirely when none of them apply.
// Delete stays gated behind the update permission too (as the menu was before), so roles that
// hold delete-without-update don't suddenly gain a card action.
const canEdit = computed(() => canBeEdited.value && hasAnyPermission('workorder.update'));
const canDelete = computed(() => canEdit.value && hasAnyPermission('workorder.delete'));
// Moving (incl. reopening a done work order) is gated on the update permission only, not !isDone —
// the per-target backend flags decide who may reopen a completed work order.
const canMove = computed(() => props.workOrder.isEditable && hasAnyPermission('workorder.update'));
const showMoveToToDo = computed(() => canMove.value && (isInProgress.value || isDone.value) && props.workOrder.canMoveToToDo);
const showMoveToInProgress = computed(() => canMove.value && (isToDo.value || isDone.value) && props.workOrder.canMoveToInProgress);
const showMoveToDone = computed(() => canMove.value && (isToDo.value || isInProgress.value) && props.workOrder.canMoveToDone);
const hasMenuActions = computed(() => showMoveToToDo.value || showMoveToInProgress.value || showMoveToDone.value || canDelete.value);

const locationHierarchy = computed(() => props.workOrder?.asset?.location?.hierarchy ?? []);
const locationLabel = computed(() => buildLocationHierarchyLabel(locationHierarchy.value));

// dueDate is a calendar date (date-only); compare whole days so the threshold doesn't
// shift with the viewer's timezone the way an hours-from-now diff would.
const daysUntilDue = computed(() => {
    if (!props.workOrder.dueDate) {
        return null;
    }

    return Math.round(DateTime.fromISO(props.workOrder.dueDate).diff(DateTime.now().startOf('day'), 'days').days);
});

const isUpcoming = computed(() => {
    if (isDone.value || daysUntilDue.value === null) {
        return false;
    }
    return daysUntilDue.value <= 2;
});

// Backend-authoritative overdue flag (WorkOrder::isOverdue) so the card, dashboard and
// notifications share one threshold instead of the card's old 24h heuristic.
const isOverdue = computed(() => workOrderIsOverdue(props.workOrder));
const daysOverdueLabel = computed(() => {
    const days = workOrderDaysOverdue(props.workOrder);
    return days > 0 ? `${days}d` : '';
});

// Neutral blue "time in status" banner: the lowest-priority banner, shown on active (non-done) cards
// that are neither in draft (attention) nor overdue. The label is the compact age since the work
// order last changed status (12m / 6h / 3d / 2w); empty when it should not show, which also hides
// the banner via the v-else-if chain.
const timeInStatusLabel = computed(() => workOrderTimeInStatusAge(props.workOrder));

const assetOperability = computed(() => props.workOrder?.asset?.operability?.value ?? props.workOrder?.asset?.status?.operability ?? null);
const assetUsageCategory = computed(() => props.workOrder?.asset?.usageCategory?.value ?? props.workOrder?.asset?.status?.usageCategory ?? null);
</script>
